import { Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatStepperModule, MatStepper } from '@angular/material/stepper';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { StateManagementService } from '../../services/state-management.service';
import { StripeService } from '../../services/stripe.service';
import { OnboardingApiService } from '../../services/onboarding-api.service';
import { BusinessDetailsComponent } from '../business-details/business-details.component';
import { RepresentativeDetailsComponent } from '../representative-details/representative-details.component';
import { DocumentUploadComponent } from '../document-upload/document-upload.component';
import { BankIntegrationComponent } from '../bank-integration/bank-integration.component';
import { DeviceSelectionComponent } from '../device-selection/device-selection.component';
import { DevicePaymentComponent } from '../device-payment/device-payment.component';
import { StatusDashboardComponent } from '../status-dashboard/status-dashboard.component';
import { BillingSetupComponent } from '../billing-setup/billing-setup.component';
import { OrgBrandingComponent } from '../org-branding/org-branding.component';

@Component({
  selector: 'app-onboarding-flow',
  standalone: true,
  imports: [
    CommonModule,
    MatStepperModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
    BusinessDetailsComponent,
    RepresentativeDetailsComponent,
    DocumentUploadComponent,
    BankIntegrationComponent,
    DeviceSelectionComponent,
    DevicePaymentComponent,
    BillingSetupComponent,
    StatusDashboardComponent,
    OrgBrandingComponent,
  ],
  templateUrl: './onboarding-flow.component.html',
  styleUrl: './onboarding-flow.component.scss',
})
export class OnboardingFlowComponent implements OnInit {
  @ViewChild('stepper') stepper!: MatStepper;
  @ViewChild('deviceSelection') deviceSelection!: DeviceSelectionComponent;

  organizationId: string | null = null;
  organization: any = null;
  isHandlingStripeReturn = false;
  isAuthVerified = false;
  resendingEmail = false;

  // Step completion flags (drive stepper editable state)
  step0Complete = false; // business details
  step1Complete = false; // branding
  step2Complete = false; // representative
  step3Complete = false; // documents
  step4Complete = false; // stripe connect
  step5Complete = false; // device selection
  step6Complete = false; // billing setup (or skipped if devices purchased with billing)
  step7Complete = false; // status/brevo

  // Device purchasing flow state
  devicePaymentPhase = false; // true when in payment, false when in selection
  skipBillingStep = false; // true if devices purchased with useForBilling=true
  deviceCart: any = null; // cart data passed to device-payment component

  constructor(
    public stateService: StateManagementService,
    private stripeService: StripeService,
    private api: OnboardingApiService,
    private snackBar: MatSnackBar,
    private route: ActivatedRoute,
    private router: Router,
  ) {}

  ngOnInit(): void {
    // Load organization from route parameter
    this.route.params.subscribe(params => {
      const orgId = params['orgId'];
      if (orgId) {
        this.api.getOrganization(orgId).subscribe({
          next: (org) => {
            this.stateService.setOrganization(org);

            // Load saved business details if they exist
            this.api.getBusinessDetails(orgId).subscribe({
              next: (details) => {
                if (details) {
                  this.stateService.setBusinessDetails(details);
                }
              },
              error: (err) => console.warn('No business details found:', err),
            });

            // Load saved representative details if they exist
            this.api.getRepresentativeDetails(orgId).subscribe({
              next: (details) => {
                if (details) {
                  this.stateService.setRepresentativeDetails(details);
                }
              },
              error: (err) => console.warn('No representative details found:', err),
            });

            // Load device orders to check if step 4 is complete
            this.api.getDeviceOrders(orgId).subscribe({
              next: (orders) => {
                if (orders && orders.length > 0) {
                  this.step4Complete = true;
                }
              },
              error: (err) => console.warn('No device orders found:', err),
            });

            // Load full onboarding status to restore all step completion flags
            this.api.getOnboardingStatus(orgId).subscribe({
              next: (status) => {
                if (status?.steps) {
                  // Restore document completion
                  if (status.steps.documents) {
                    this.step2Complete = true;
                    this.stateService.setDocumentsComplete([]);
                  }
                  // Restore Stripe Connect
                  if (status.steps.stripeConnect) {
                    this.step3Complete = true;
                    this.stateService.setStripeStatus('active');
                  }
                  // Restore Brevo
                  if (status.steps.brevo) {
                    this.stateService.setBrevoConfigured({ apiKey: '', senderName: '', senderEmail: '' });
                  }
                }
                // Restore billing setup from org doc
                if (org.BillingSetupComplete) {
                  this.step5Complete = true;
                  this.stateService.setBillingActive({
                    bankName: org.BillingBankName || '',
                    bankLast4: org.BillingBankLast4 || '',
                    customerId: org.StripeCustomerId || '',
                  });
                }
                // Restore provisioning status
                if (status?.provisioningStatus) {
                  this.stateService.setProvisioningStatus(status.provisioningStatus);
                }
              },
              error: (err) => console.warn('Onboarding status not available:', err),
            });
          },
          error: (err) => {
            console.error('Failed to load organization:', err);
            this.snackBar.open('Failed to load organization. Redirecting...', 'Close', { duration: 5000 });
            this.router.navigate(['/register']);
          }
        });
      }
    });

    this.stateService.organization$.subscribe(org => {
      this.organization = org;
      this.organizationId = org?.id || null;
      // Check if org is verified by looking at AuthVerifiedAt field
      this.isAuthVerified = !!org?.AuthVerifiedAt;
    });

    this.stateService.state$.subscribe(state => {
      this.step0Complete = !!state.businessDetails;
      // step1 (branding) is optional — tracked via onBrandingComplete/onBrandingSkipped
      this.step2Complete = !!state.representativeDetails;
      this.step3Complete = state.documentsComplete;
      this.step4Complete = state.stripeStatus === 'active';
      // step5 (devices) is tracked via onDeviceSelectionComplete/onDevicePaymentComplete
      // step6 (billing) depends on device payment result
    });

    // Handle Stripe redirect return
    const stripeReturn = this.stripeService.detectStripeReturn();
    if (stripeReturn.isReturn || stripeReturn.isRefresh) {
      this.isHandlingStripeReturn = true;
      const orgId = stripeReturn.orgId || this.organizationId;
      if (orgId) {
        this.api.getOrganization(orgId).subscribe({
          next: (org) => {
            this.stateService.setOrganization(org);
            // Jump to the Stripe step (step 4 now, after branding inserted at 1)
            this.isHandlingStripeReturn = false;
            setTimeout(() => {
              if (this.stepper) this.stepper.selectedIndex = 4;
            }, 300);
          },
          error: () => { this.isHandlingStripeReturn = false; }
        });
      } else {
        this.isHandlingStripeReturn = false;
      }
      // Clean URL query params
      const url = window.location.pathname;
      window.history.replaceState({}, '', url);
    }

    // Auto-advance to first incomplete step on reload (after all async loads complete)
    setTimeout(() => {
      let firstIncomplete = 0;
      if (this.step0Complete) firstIncomplete = 1;
      if (this.step1Complete) firstIncomplete = 2;
      if (this.step2Complete) firstIncomplete = 3;
      if (this.step3Complete) firstIncomplete = 4;
      if (this.step4Complete) firstIncomplete = 5;
      if (this.step5Complete && !this.skipBillingStep) firstIncomplete = 6;
      if (this.step6Complete) firstIncomplete = 7;

      if (this.stepper && firstIncomplete > 0) {
        this.stepper.selectedIndex = firstIncomplete;
      }
    }, 3000); // Wait for getOnboardingStatus + device orders + org load
  }

  /**
   * Check if user can proceed to step (for steps requiring auth).
   * Steps 3-5 require confirmed org auth.
   */
  canProceedToStep(stepIndex: number): boolean {
    // Steps 0-2 don't require auth verification
    if (stepIndex < 3) {
      return true;
    }
    // Steps 3-5 require verified auth
    if (!this.isAuthVerified) {
      this.snackBar.open('Please verify your email before accessing this step.', 'Close', { duration: 5000 });
      return false;
    }
    return true;
  }

  onBusinessDetailsComplete(): void {
    this.step0Complete = true;
    setTimeout(() => this.stepper?.next(), 100);
  }

  onBrandingComplete(): void {
    this.step1Complete = true;
    setTimeout(() => this.stepper?.next(), 100);
  }

  onBrandingSkipped(): void {
    this.step1Complete = true; // Mark complete so stepper doesn't block progression
    setTimeout(() => this.stepper?.next(), 100);
  }

  onRepresentativeDetailsComplete(): void {
    this.step2Complete = true;
    setTimeout(() => this.stepper?.next(), 100);
  }

  onDocumentsComplete(): void {
    this.step3Complete = true;
    setTimeout(() => this.stepper?.next(), 100);
  }

  onStripeComplete(): void {
    this.step4Complete = true;
    setTimeout(() => this.stepper?.next(), 100);
  }

  onDeviceSelectionComplete(cart: any): void {
    if (cart.items && cart.items.length > 0) {
      this.deviceCart = cart;
      this.devicePaymentPhase = true;
    } else {
      this.step5Complete = true;
      setTimeout(() => this.stepper?.next(), 100);
    }
  }

  onDevicePaymentComplete(result: any): void {
    this.step5Complete = true;
    this.skipBillingStep = result.useForBilling;
    this.devicePaymentPhase = false;

    setTimeout(() => {
      if (this.deviceSelection) {
        this.deviceSelection.refreshOrders();
      }
    }, 1000);

    setTimeout(() => {
      if (this.skipBillingStep) {
        this.step6Complete = true;
        this.stepper?.next();
        this.stepper?.next();
      } else {
        this.stepper?.next();
      }
    }, 2000);
  }

  onDevicePaymentBack(): void {
    this.devicePaymentPhase = false;
  }

  onDeviceSkipped(): void {
    this.step5Complete = true;
    this.skipBillingStep = false;
    this.devicePaymentPhase = false;
    setTimeout(() => this.stepper?.next(), 100);
  }

  onBillingSetupComplete(): void {
    this.step6Complete = true;
    setTimeout(() => this.stepper?.next(), 100);
  }

  onOnboardingComplete(): void {
    this.step7Complete = true;
    // Lock the stepper — onboarding is done, all further changes via OrgConsole
    if (this.stepper) {
      this.stepper.linear = true;
    }
  }

  resendVerificationEmail(): void {
    if (!this.organizationId) {
      this.snackBar.open('Organization ID not found.', 'Close', { duration: 5000 });
      return;
    }
    this.resendingEmail = true;
    this.api.resendOrgVerification(this.organizationId).subscribe({
      next: () => {
        this.snackBar.open('Verification email sent! Check your inbox.', 'Close', { duration: 5000 });
        this.resendingEmail = false;
      },
      error: (err) => {
        console.error('Failed to resend email:', err);
        this.snackBar.open('Failed to resend email. Please try again.', 'Close', { duration: 5000 });
        this.resendingEmail = false;
      }
    });
  }

  startOver(): void {
    this.stateService.reset();
  }
}
